import React, { useEffect, useState } from "react";
import {
  Avatar, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, MenuItem, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CallIcon from "@mui/icons-material/Call";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import StarIcon from "@mui/icons-material/Star";
import CheckIcon from "@mui/icons-material/Check";
import API from "../../API";
import { formatDate, money, HOLD_LABELS, OUTCOME_LABELS, STATUS_LABELS } from "./MarketplaceBase";

/*
 * Full-page marketplace request detail. Styled to match the panel's order
 * detail screen (OrderScreen): #F8FAFC canvas, white 16px-radius cards with
 * the #E8EDF5 border + soft slate shadow, uppercase eyebrow labels and the
 * indigo accent palette. Standalone from the legacy order screens.
 */

const CARD_STYLE = {
  backgroundColor: "#FFFFFF",
  borderRadius: 16,
  border: "1px solid #E8EDF5",
  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.05)",
};
const EYEBROW_STYLE = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: "#94A3B8",
  marginBottom: 6,
};
const SECTION_TITLE_STYLE = {
  fontSize: 13,
  fontWeight: 800,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "#0F172A",
};
const HEADER_ICON_BUTTON_STYLE = {
  border: "1px solid #E2E8F0",
  borderRadius: 12,
  width: 42,
  height: 42,
};

const getStatusChipStyle = (status) => {
  if (["completed", "contract_signed", "worked_with"].includes(status))
    return { backgroundColor: "#DCFCE7", color: "#15803D" };
  if (["contract_pending", "mover_selected", "driver_selected"].includes(status))
    return { backgroundColor: "#FEF3C7", color: "#B45309" };
  if (["cancelled", "not_completed"].includes(status))
    return { backgroundColor: "#FEE2E2", color: "#B91C1C" };
  return { backgroundColor: "#EEF2FF", color: "#4F46E5" };
};

const CONTRACT_STATUS_LABELS = {
  draft: "Taslak",
  awaiting_customer: "Müşteri imzası bekleniyor",
  awaiting_driver: "Sürücü imzası bekleniyor",
  customer_signed: "Müşteri imzaladı",
  driver_signed: "Sürücü imzaladı",
  fully_signed: "İki taraf da imzaladı",
  void: "Geçersiz",
};
const QUOTE_STATUS_LABELS = {
  active: "Aktif",
  held: "Bloke edildi",
  contacted: "İletişime geçildi",
  selected: "Seçildi",
  not_selected: "Seçilmedi",
  withdrawn: "Geri çekildi",
  expired: "Süresi doldu",
  closed: "Kapandı",
};
const PAYMENT_LABELS = { cash: "Nakit", card: "Kredi kartı", either: "Fark etmez" };
const PACKED_LABELS = { ready: "Paketli ve hazır", needed: "Paketleme gerekli" };
const VEHICLE_LABELS = {
  doblo: "Doblo", panel_van: "Panelvan", pickup_truck: "Kamyonet",
  "6_wheel": "6 Teker Kamyon", "8_wheel": "8 Teker Kamyon",
  "10_wheel": "10 Teker Kamyon", lorry: "Tır",
  // Quote values stored as English display text by older records.
  "Panel Van": "Panelvan", "Pickup Truck": "Kamyonet", "6-Wheel": "6 Teker",
  "8-Wheel": "8 Teker", "10-Wheel": "10 Teker", "6-Wheel Truck": "6 Teker Kamyon",
  "8-Wheel Truck": "8 Teker Kamyon", "10-Wheel Truck": "10 Teker Kamyon",
  Lorry: "Tır", "Closed Box": "Kapalı Kasa", "Curtain-Sided": "Tenteli", "Open Body": "Açık Kasa",
};
const PROPERTY_TYPE_LABELS = {
  studio: "Stüdyo", "1_1": "1+1", "2_1": "2+1", "3_1": "3+1", "4_1": "4+1",
  duplex: "Dubleks", villa: "Villa", triplex: "Tripleks", villa_duplex: "Villa/Dubleks", office: "Ofis",
  Studio: "Stüdyo", "1+1": "1+1", "2+1": "2+1", "3+1": "3+1", "4+1": "4+1",
  Duplex: "Dubleks", Villa: "Villa", Triplex: "Tripleks", "Villa/Duplex": "Villa/Dubleks", Office: "Ofis",
};
const ELEVATOR_LABELS = { yes: "Asansör uygun", some_do_not_fit: "Bazı eşyalar sığmıyor", no_elevator: "Asansör yok" };
const EXTERIOR_LABELS = { yes: "Gerekli", no: "Gerekli değil", not_sure: "Belirsiz", included: "Dahil", not_included: "Dahil değil", not_required: "Gerekli değil" };
const PACKING_LABELS = { mover_packs_all: "Tümü nakliyeci paketler", boxes_supplied: "Koliler nakliyeci sağlar", self_packed: "Müşteri paketler", customer_packs: "Müşteri paketler" };
const BOX_LABELS = { "0_10": "0–10 koli", "10_20": "10–20 koli", "20_30": "20–30 koli", "30_plus": "30+ koli", over_30: "30+ koli" };
const GOODS_LABELS = { "50_100": "50B–100B TL", "100_250": "100B–250B TL", "250_500": "250B–500B TL", "500_1000": "500B–1M TL", over_1000: "1M TL üzeri", custom: "Özel tutar" };
const RULE_LABELS = { restricted_hours: "Gün/saat kısıtı", management_approval: "Yönetim onayı", freight_elevator_reservation: "Yük asansörü rezervasyonu", security_details: "Güvenlik bilgisi", common_area_protection: "Ortak alan koruması", no_rules: "Kural yok", not_sure: "Belirsiz" };
const CANCELLED_BY_LABELS = { customer: "Müşteri", admin: "Yönetici" };
const CANCEL_REASONS = [
  "Müşteri talebi üzerine",
  "Mükerrer talep",
  "Müşteriye ulaşılamadı",
  "Hatalı / eksik bilgi",
  "Uygunsuz içerik",
  "Diğer",
];
const floorLabel = (value) => (value == null || value === "" ? "-" : value === "ground" ? "Zemin kat" : value === "22_plus" ? "22+ kat" : value === "6_plus" ? "6+ kat" : `${value}. kat`);
const listLabel = (values, map) => (Array.isArray(values) && values.length ? values.map((v) => (map ? map[v] || v : v)).join(", ") : "-");
const yesNo = (value) => (value === true ? "Evet" : value === false ? "Hayır" : "-");
const remainingText = (endsAt) => {
  if (!endsAt) return null;
  const ms = new Date(endsAt).getTime() - Date.now();
  if (Number.isNaN(ms) || ms <= 0) return null;
  const minutes = Math.round(ms / 60000);
  return minutes < 60 ? `${minutes} dk kaldı` : `${Math.floor(minutes / 60)} sa ${minutes % 60} dk kaldı`;
};
const ITEM_LABELS = {
  sofa: "3'lü koltuk", refrigerator: "Buzdolabı", double_bed: "Çift kişilik yatak",
  wardrobe: "Gardırop", television: "Televizyon", armchair: "Tekli koltuk",
  washing_machine: "Çamaşır makinesi", dining_table: "Yemek masası", other: "Diğer",
};
const RANGE_LABELS = { within_week: "1 hafta içinde", within_month: "1 ay içinde", over_month: "1 aydan sonra" };
const INSURANCE_LABELS = { insured: "Sigortalı", uninsured: "Sigortasız", included: "Fiyata dahil", compare: "Sigortalı / sigortasız karşılaştırma" };
const label = (map, value) => (value ? map[value] || value : "-");

function StatusPill({ status }) {
  return (
    <div
      style={{
        ...getStatusChipStyle(status),
        display: "inline-block",
        padding: "4px 12px",
        borderRadius: 8,
        fontSize: 12,
        fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      {STATUS_LABELS[status] || status || "-"}
    </div>
  );
}

function SectionCard({ icon, iconBackground, iconColor, title, children, style }) {
  return (
    <div style={{ ...CARD_STYLE, display: "flex", flexDirection: "column", ...style }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "16px 20px",
          borderBottom: "1px solid #EEF2F7",
        }}
      >
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: 10,
            backgroundColor: iconBackground || "#EEF2FF",
            color: iconColor || "#4F46E5",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
        <Typography style={SECTION_TITLE_STYLE}>{title}</Typography>
      </div>
      <div style={{ padding: "8px 20px 16px 20px", flex: 1 }}>{children}</div>
    </div>
  );
}

const PAYMENT_METHOD_LABELS = { cash: "Nakit", card: "Kart", bank_transfer: "Havale/EFT" };
const SERVICE_LABELS = { wrapping: "Sarma", packing: "Paketleme", disassembly: "Söküm / kurulum", wallRemounting: "Duvar montajı", unloading: "İndirme yardımı" };
const DATE_AVAILABILITY_LABELS = { customer_date: "Müşterinin tarihinde uygun", alternative: "Alternatif tarih önerildi" };

/*
 * Every value a driver submitted with the offer, as label/value pairs, so the
 * Offers table can show the complete offer in a side panel instead of sending
 * operations into another page.
 */
function offerRows(quote) {
  const details = quote.details || {};
  const services = { wrapping: true, ...(details.includedServices || quote.includedServices || {}) };
  const included = Object.entries(services).filter(([, on]) => on).map(([key]) => SERVICE_LABELS[key] || key);
  const excluded = Object.entries(services).filter(([, on]) => !on).map(([key]) => SERVICE_LABELS[key] || key);
  const rows = [];

  if (quote.insurance === "compare" || details.insuredPrice || details.uninsuredPrice) {
    rows.push(["Sigortalı fiyat", money(details.insuredPrice, quote.currency)]);
    rows.push(["Sigortasız fiyat", money(details.uninsuredPrice, quote.currency)]);
  } else {
    rows.push(["Toplam fiyat", money(quote.price, quote.currency)]);
  }
  rows.push(["Sigorta", label(INSURANCE_LABELS, quote.insurance)]);
  if (details.insuranceAmount) rows.push(["Sigorta teminatı", money(details.insuranceAmount, quote.currency)]);
  if (quote.teamSize != null) rows.push(["Ekip büyüklüğü", `${quote.teamSize} kişi`]);
  if (details.durationHours != null) rows.push(["Tahmini süre", `${details.durationHours} saat`]);
  if (details.dateAvailability) rows.push(["Tarih", label(DATE_AVAILABILITY_LABELS, details.dateAvailability)]);
  if (details.alternativeDate) rows.push(["Alternatif tarih", formatDate(details.alternativeDate)]);
  if (quote.pickupDate) rows.push(["Alım tarihi", formatDate(quote.pickupDate)]);
  if (quote.deliveryDate) rows.push(["Tahmini teslim tarihi", formatDate(quote.deliveryDate)]);
  rows.push(["Araç", [label(VEHICLE_LABELS, quote.vehicle), details.vehicleBody ? label(VEHICLE_LABELS, details.vehicleBody) : null].filter(Boolean).join(" · ") || "-"]);
  if (quote.routeStatus) rows.push(["Araç yük durumu", quote.routeStatus]);
  if (details.exteriorLift) rows.push(["Dış cephe asansörü", label(EXTERIOR_LABELS, details.exteriorLift)]);
  if ((details.paymentMethods || []).length) rows.push(["Ödeme yöntemleri", details.paymentMethods.map((value) => PAYMENT_METHOD_LABELS[value] || value).join(" · ")]);
  rows.push(["Dahil hizmetler", included.join(", ") || "-"]);
  rows.push(["Hariç hizmetler", excluded.join(", ") || "-"]);
  return rows;
}

function DetailRow({ label: rowLabel, value, last }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 16,
        padding: "10px 0",
        borderBottom: last ? "none" : "1px solid #F1F5F9",
      }}
    >
      <Typography style={{ fontSize: 13, color: "#64748B", flexShrink: 0 }}>{rowLabel}</Typography>
      <Typography style={{ fontSize: 13, fontWeight: 700, color: "#0F172A", textAlign: "right", overflowWrap: "anywhere", minWidth: 0 }}>
        {value ?? "-"}
      </Typography>
    </div>
  );
}

function DriverLink({ driverId, name, history }) {
  if (!driverId) return <Typography style={{ fontSize: 13, fontWeight: 700, color: "#0F172A" }}>{name || "-"}</Typography>;
  return (
    <div
      style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer" }}
      onClick={() => history.push(`/driver/${driverId}`)}
    >
      <Avatar sx={{ width: 26, height: 26, bgcolor: "#EEF2FF", color: "#4F46E5", fontSize: 12, fontWeight: 700 }}>
        {(name || "?").charAt(0).toUpperCase()}
      </Avatar>
      <Typography style={{ fontSize: 13, fontWeight: 700, color: "#0F172A" }}>{name || "-"}</Typography>
      <OpenInNewIcon sx={{ fontSize: 14, color: "#64748B" }} />
    </div>
  );
}

function JourneySteps({ data, isIntercity }) {
  const request = data.request;
  const closedEarly = ["cancelled", "expired", "auto_closed", "expired_no_selection"].includes(request.status) && !request.outcome;
  const hasSelection = !!(request.selectedQuoteId || data.selection);
  const contractSigned = !!(data.contract && data.contract.customerSignedAt && data.contract.driverSignedAt);
  const hasOutcome = !!request.outcome;
  const hasReview = !!data.review;

  const steps = [];
  steps.push({
    label: "Talep yayınlandı · Teklifler toplanıyor",
    hint: formatDate(request.createdAt),
    done: request.status !== "quotes_open",
  });
  steps.push({
    label: isIntercity ? "Sürücü seçildi" : "Nakliyeci seçildi",
    hint: hasSelection && data.selection?.agreedMoveStartsAt ? formatDate(data.selection.agreedMoveStartsAt) : null,
    done: hasSelection,
  });
  if (!isIntercity) {
    steps.push({
      optional: true,
      label: "Sözleşme onaylandı (opsiyonel)",
      hint: contractSigned
        ? formatDate(data.contract.customerSignedAt > data.contract.driverSignedAt ? data.contract.customerSignedAt : data.contract.driverSignedAt)
        : data.contract
          ? label(CONTRACT_STATUS_LABELS, data.contract.status)
          : null,
      done: contractSigned,
    });
  }
  steps.push({
    label: isIntercity ? "Taşıma günü · Sonuç bildirildi" : "Taşınma günü · Sonuç bildirildi",
    hint: hasOutcome ? OUTCOME_LABELS[request.outcome] || request.outcome : null,
    done: hasOutcome,
  });
  steps.push({
    label: "Müşteri değerlendirmesi",
    hint: hasReview ? `★ ${data.review.rating} / 5` : null,
    done: hasReview,
  });

  let currentIndex = steps.findIndex((step) => !step.done && !step.optional);
  if (currentIndex === -1) currentIndex = steps.length;

  return (
    <SectionCard
      icon={<RouteOutlinedIcon sx={{ fontSize: 19 }} />}
      title="Sipariş Yolculuğu"
    >
      {closedEarly ? (
        <div style={{ backgroundColor: "#FEE2E2", color: "#B91C1C", borderRadius: 10, padding: "10px 14px", margin: "10px 0", fontSize: 13, fontWeight: 700 }}>
          Talep tamamlanmadan kapandı — {STATUS_LABELS[request.status] || request.status}
          {request.closureReason ? ` · ${OUTCOME_LABELS[request.closureReason] || request.closureReason}` : ""}
        </div>
      ) : null}
      <div style={{ padding: "4px 0" }}>
        {steps.map((step, index) => {
          const state = step.done ? "done" : index === currentIndex && !closedEarly ? "current" : "pending";
          const isLast = index === steps.length - 1;
          return (
            <div key={step.label} style={{ display: "flex", gap: 10 }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <div
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: 11,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    backgroundColor: state === "done" ? "#DCFCE7" : state === "current" ? "#4F46E5" : "#F1F5F9",
                    border: state === "current" ? "none" : "1px solid " + (state === "done" ? "#BBF7D0" : "#E2E8F0"),
                  }}
                >
                  {state === "done" ? (
                    <CheckIcon sx={{ fontSize: 14, color: "#15803D" }} />
                  ) : state === "current" ? (
                    <div style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: "#FFFFFF" }} />
                  ) : (
                    <div style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: "#CBD5E1" }} />
                  )}
                </div>
                {!isLast ? (
                  <div style={{ width: 2, flex: 1, minHeight: 8, backgroundColor: state === "done" ? "#BBF7D0" : "#E8EDF5", margin: "3px 0" }} />
                ) : null}
              </div>
              <div style={{ paddingBottom: isLast ? 2 : 10, minWidth: 0 }}>
                <Typography
                  style={{
                    fontSize: 12.5,
                    fontWeight: state === "current" ? 800 : 700,
                    color: state === "pending" ? "#94A3B8" : "#0F172A",
                    lineHeight: "22px",
                  }}
                >
                  {step.label}
                  {state === "current" ? (
                    <span style={{ marginLeft: 8, backgroundColor: "#EEF2FF", color: "#4F46E5", padding: "2px 8px", borderRadius: 7, fontSize: 10, fontWeight: 800, verticalAlign: "middle" }}>
                      ŞU ANKİ ADIM
                    </span>
                  ) : null}
                </Typography>
                {step.hint ? (
                  <Typography style={{ fontSize: 11.5, color: "#64748B", marginTop: -3 }}>{step.hint}</Typography>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}

const headCellSx = {
  fontSize: 12,
  fontWeight: 800,
  color: "#64748B",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  borderBottom: "1px solid #EEF2F7",
  whiteSpace: "nowrap",
};
const bodyCellSx = { fontSize: 13, color: "#0F172A", borderBottom: "1px solid #F1F5F9" };

export default function MarketplaceRequestScreen({ serviceType, history, match }) {
  const requestId = match?.params?.id;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionDialog, setActionDialog] = useState(null); // "cancel" | "edit" | "note"
  const [openedQuote, setOpenedQuote] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [noteText, setNoteText] = useState("");
  const [editDate, setEditDate] = useState("");
  const [editPayment, setEditPayment] = useState("");
  const [media, setMedia] = useState(null);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [openedMedia, setOpenedMedia] = useState(null);

  const runAction = (action, body) => {
    setActionBusy(true);
    API.fetchClient("panel/managemarketplacerequest", { serviceType, requestId, action, ...body }).then((result) => {
      setActionBusy(false);
      if (result?.ok) { setActionDialog(null); setCancelReason(""); setNoteText(""); setEditDate(""); setEditPayment(""); load(); }
    });
  };
  const loadMedia = () => {
    setMediaLoading(true);
    API.fetchClient("panel/getmarketplacemedia", { serviceType, requestId }).then((result) => {
      setMediaLoading(false);
      setMedia(result?.items || []);
    });
  };

  const load = () => {
    setLoading(true);
    API.fetchClient("panel/getmarketplacerequest", { serviceType, requestId }).then((result) => {
      setLoading(false);
      if (result?.request) setData(result);
    });
  };
  useEffect(() => { if (requestId) load(); }, [requestId, serviceType]);

  const isIntercity = serviceType === "intercity";
  const payload = data?.request?.payload || {};
  const twoCol = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))",
    gap: 20,
    marginTop: 20,
    alignItems: "stretch",
  };

  return (
    <div style={{ display: "block", width: "100%", backgroundColor: "#F8FAFC", flexShrink: 0, paddingBottom: 80 }}>
      <div style={{ width: "100%", padding: "0 12px", boxSizing: "border-box", maxWidth: "100%", overflowX: "hidden" }}>
        {/* Header card — same pattern as the order detail screen */}
        <div
          style={{
            ...CARD_STYLE,
            marginTop: 24,
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 20,
            padding: "20px 24px",
          }}
        >
          <Tooltip title="Geri">
            <IconButton style={HEADER_ICON_BUTTON_STYLE} onClick={() => history.goBack()}>
              <ArrowBackIcon sx={{ fontSize: 20, color: "#334155" }} />
            </IconButton>
          </Tooltip>
          <div style={{ flex: 1, minWidth: 160 }}>
            <Typography style={EYEBROW_STYLE}>{isIntercity ? "Şehirlerarası Talep" : "Evden Eve Talep"}</Typography>
            <Typography
              style={{ fontSize: 24, lineHeight: 1.1, fontWeight: 800, letterSpacing: "-0.02em", color: "#0F172A" }}
            >
              #{requestId ? requestId.slice(-8).toUpperCase() : "-"}
            </Typography>
            {data?.request?.createdAt ? (
              <Typography style={{ marginTop: 6, color: "#64748B", fontSize: 13 }}>
                Oluşturulma: {formatDate(data.request.createdAt)}
              </Typography>
            ) : null}
          </div>
          {/* Customer identity sits next to the request number so operations
              can reach the person without scrolling to the Müşteri card. */}
          {data?.customer ? (
            <div style={{ minWidth: 200 }}>
              <Typography style={EYEBROW_STYLE}>Müşteri</Typography>
              <Typography
                onClick={() => data.customer.customerId && history.push(`/client/${data.customer.customerId}`)}
                style={{ fontSize: 15, fontWeight: 800, color: data.customer.customerId ? "#4F46E5" : "#0F172A", cursor: data.customer.customerId ? "pointer" : "default" }}
              >
                {data.customer.name || "-"}
              </Typography>
              <Typography style={{ color: "#64748B", fontSize: 13 }}>{data.customer.phone || "-"}</Typography>
              {data.customer.email ? <Typography style={{ color: "#64748B", fontSize: 13 }}>{data.customer.email}</Typography> : null}
            </div>
          ) : null}
          <div>
            <Typography style={EYEBROW_STYLE}>Durum</Typography>
            {data ? <StatusPill status={data.request.status} /> : <Typography style={{ color: "#94A3B8" }}>-</Typography>}
            {data?.request?.closureReason ? (
              <Typography style={{ marginTop: 6, color: "#64748B", fontSize: 12, fontWeight: 600 }}>
                {OUTCOME_LABELS[data.request.closureReason] || data.request.closureReason}
              </Typography>
            ) : null}
          </div>
          {data?.request?.quoteWindowEndsAt ? (
            <div>
              <Typography style={EYEBROW_STYLE}>Teklif Penceresi</Typography>
              <Typography style={{ fontSize: 14, fontWeight: 800, color: "#0F172A" }}>{formatDate(data.request.quoteWindowEndsAt)}</Typography>
              {data.request.status === "quotes_open" && remainingText(data.request.quoteWindowEndsAt) ? (
                <Typography style={{ fontSize: 12, fontWeight: 700, color: "#B45309", marginTop: 2 }}>{remainingText(data.request.quoteWindowEndsAt)}</Typography>
              ) : null}
            </div>
          ) : null}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {data && ["quotes_open", "mover_selected", "driver_selected", "contract_pending"].includes(data.request.status) ? (
              <button type="button" onClick={() => setActionDialog("cancel")} style={{ padding: "8px 14px", borderRadius: 10, border: "1px solid #F3C9C9", backgroundColor: "#FDF3F3", color: "#B91C1C", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Talebi İptal Et</button>
            ) : null}
            {data && data.request.status === "quotes_open" ? (
              <button type="button" onClick={() => setActionDialog("edit")} style={{ padding: "8px 14px", borderRadius: 10, border: "1px solid #E2E8F0", backgroundColor: "#FFFFFF", color: "#0F172A", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Düzenle</button>
            ) : null}
            {data ? (
              <button type="button" onClick={() => setActionDialog("note")} style={{ padding: "8px 14px", borderRadius: 10, border: "1px solid #E2E8F0", backgroundColor: "#FFFFFF", color: "#0F172A", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Not Ekle</button>
            ) : null}
          </div>
          <Tooltip title="Yenile">
            <IconButton style={HEADER_ICON_BUTTON_STYLE} onClick={load}>
              <RefreshIcon sx={{ fontSize: 20, color: "#334155" }} />
            </IconButton>
          </Tooltip>
        </div>

        {!data || loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}><CircularProgress /></Box>
        ) : (
          <>
            <div style={twoCol}>
              <SectionCard
                icon={<RouteOutlinedIcon sx={{ fontSize: 19 }} />}
                title="Alış Bilgileri"
              >
                {isIntercity ? (
                  <>
                    <DetailRow label="Adres" value={payload.route?.pickup?.address?.label} />
                    <DetailRow label="Kat" value={floorLabel(payload.route?.pickup?.floor)} />
                    <DetailRow label="Asansör" value={ELEVATOR_LABELS[payload.route?.pickup?.elevatorFit] || payload.route?.pickup?.elevatorFit || "-"} />
                    <DetailRow label="Taşımayı kim yapacak" value={{ customer: "Müşteri", shared: "Sürücü yardım eder", driver: "Sürücü" }[payload.route?.pickup?.carryingResponsibility] || "-"} last />
                  </>
                ) : (
                  <>
                    <DetailRow label="Adres" value={payload.pickup?.exactAddress || payload.pickup?.label || payload.pickup?.address} />
                    <DetailRow label="Konut tipi" value={label(PROPERTY_TYPE_LABELS, payload.pickup?.propertyType)} />
                    <DetailRow label="Kat" value={floorLabel(payload.pickup?.floor)} />
                    <DetailRow label="Asansör" value={ELEVATOR_LABELS[payload.pickup?.elevatorFit] || payload.pickup?.elevatorFit || "-"} />
                    <DetailRow label="Dış cephe asansörü" value={EXTERIOR_LABELS[payload.logistics?.crane?.pickup] || payload.logistics?.crane?.pickup || "-"} />
                    <DetailRow label="Bina kuralları" value={listLabel(payload.logistics?.buildingPolicy?.pickup?.rules, RULE_LABELS)} />
                    {payload.logistics?.buildingPolicy?.pickup?.note ? <DetailRow label="Bina notu" value={payload.logistics.buildingPolicy.pickup.note} last /> : <DetailRow label="" value="" last />}
                  </>
                )}
              </SectionCard>

              <SectionCard
                icon={<RouteOutlinedIcon sx={{ fontSize: 19 }} />}
                title="Teslimat Bilgileri"
                iconBackground="#ECFDF5"
                iconColor="#059669"
              >
                {isIntercity ? (
                  <>
                    <DetailRow label="Adres" value={payload.route?.delivery?.address?.label} />
                    <DetailRow label="Kat" value={floorLabel(payload.route?.delivery?.floor)} />
                    <DetailRow label="Asansör" value={ELEVATOR_LABELS[payload.route?.delivery?.elevatorFit] || payload.route?.delivery?.elevatorFit || "-"} />
                    <DetailRow label="Taşımayı kim yapacak" value={{ customer: "Müşteri", shared: "Sürücü yardım eder", driver: "Sürücü" }[payload.route?.delivery?.carryingResponsibility] || "-"} last />
                  </>
                ) : (
                  <>
                    <DetailRow label="Adres" value={payload.dropoff?.exactAddress || payload.dropoff?.label || payload.dropoff?.address} />
                    <DetailRow label="Konut tipi" value={label(PROPERTY_TYPE_LABELS, payload.dropoff?.propertyType)} />
                    <DetailRow label="Kat" value={floorLabel(payload.dropoff?.floor)} />
                    <DetailRow label="Asansör" value={ELEVATOR_LABELS[payload.dropoff?.elevatorFit] || payload.dropoff?.elevatorFit || "-"} />
                    <DetailRow label="Dış cephe asansörü" value={EXTERIOR_LABELS[payload.logistics?.crane?.dropoff] || payload.logistics?.crane?.dropoff || "-"} />
                    <DetailRow label="Bina kuralları" value={listLabel(payload.logistics?.buildingPolicy?.delivery?.rules, RULE_LABELS)} />
                    {payload.logistics?.buildingPolicy?.delivery?.note ? <DetailRow label="Bina notu" value={payload.logistics.buildingPolicy.delivery.note} last /> : <DetailRow label="" value="" last />}
                  </>
                )}
              </SectionCard>

              <SectionCard
                icon={<LocalOfferOutlinedIcon sx={{ fontSize: 19 }} />}
                title="Talep Bilgileri"
                iconBackground="#FEF6E7"
                iconColor="#D97706"
              >
                {isIntercity ? (
                  <>
                    <DetailRow label="Tür" value={payload.mode === "one_item" ? "Parça Eşya" : (VEHICLE_LABELS[payload.requestedVehicle] || payload.requestedVehicle || "Büyük yük")} />
                    <DetailRow label="Tarih" value={payload.schedule?.selectedDate ? formatDate(payload.schedule.selectedDate) : label(RANGE_LABELS, payload.schedule?.flexibleRange)} />
                    <DetailRow label="Ödeme tercihi" value={label(PAYMENT_LABELS, payload.paymentPreference || payload.logistics?.paymentPreference)} />
                    {payload.mode === "one_item" ? <>
                      <DetailRow label="Eşya" value={label(ITEM_LABELS, payload.cargo?.itemType)} />
                      <DetailRow label="Ölçüler" value={payload.cargo?.dimensions} />
                      <DetailRow label="Ağırlık" value={payload.cargo?.weight} />
                    </> : <DetailRow label="Yük açıklaması" value={payload.cargo?.description} />}
                    <DetailRow label="Paket durumu" value={label(PACKED_LABELS, payload.cargo?.packedStatus)} />
                    {payload.schedule?.note ? <DetailRow label="Tarih notu" value={payload.schedule.note} /> : null}
                    {payload.cargo?.note ? <DetailRow label="Müşteri notu" value={payload.cargo.note} /> : null}
                    <DetailRow label="Medya" value={`${data.request.mediaCount?.photos ?? 0} fotoğraf · ${data.request.mediaCount?.video ?? 0} video`} last />
                  </>
                ) : (
                  <>
                    <DetailRow label="Ev tipi" value={label(PROPERTY_TYPE_LABELS, payload.homeType) || payload.homeType} />
                    <DetailRow label="Tarih" value={payload.movingDate?.startsAt ? formatDate(payload.movingDate.startsAt) : label(RANGE_LABELS, payload.movingDate?.approximateTiming)} />
                    <DetailRow label="Ödeme tercihi" value={label(PAYMENT_LABELS, payload.paymentPreference || payload.logistics?.paymentPreference)} />
                    <DetailRow label="Paketleme" value={label(PACKING_LABELS, payload.services?.packing)} />
                    <DetailRow label="Koli sayısı" value={label(BOX_LABELS, payload.services?.boxCount)} />
                    <DetailRow label="Söküm / kurulum" value={(payload.services?.assemblyItems || []).length ? listLabel(payload.services.assemblyItems) : yesNo(payload.services?.assembly)} />
                    <DetailRow label="Duvar montajı" value={(payload.services?.wallMountedItems || []).length ? listLabel(payload.services.wallMountedItems) : yesNo(payload.services?.wallMounted)} />
                    <DetailRow label="Özel eşya" value={(payload.services?.specialCareItems || []).length ? listLabel(payload.services.specialCareItems) : yesNo(payload.services?.specialCare)} />
                    <DetailRow label="Sigorta tercihi" value={label(INSURANCE_LABELS, payload.logistics?.insurancePreference)} />
                    <DetailRow label="Tahmini eşya değeri" value={label(GOODS_LABELS, payload.logistics?.goodsValueBand) || payload.logistics?.customGoodsValue} />
                    {payload.movingDate?.note ? <DetailRow label="Tarih notu" value={payload.movingDate.note} /> : null}
                    {payload.services?.specialCareNote ? <DetailRow label="Müşteri notu" value={payload.services.specialCareNote} /> : null}
                    <DetailRow label="Medya" value={`${data.request.mediaCount?.photos ?? 0} fotoğraf · ${data.request.mediaCount?.video ?? 0} video`} last />
                  </>
                )}
                {(data.request.mediaCount?.photos || 0) + (data.request.mediaCount?.video || 0) > 0 ? (
                  <div style={{ marginTop: 10 }}>
                    {media === null ? (
                      <button type="button" disabled={mediaLoading} onClick={loadMedia} style={{ padding: "8px 14px", borderRadius: 10, border: "1px solid #E2E8F0", backgroundColor: "#FFFFFF", color: "#4F46E5", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                        {mediaLoading ? "Yükleniyor…" : "Müşteri Medyasını Gör"}
                      </button>
                    ) : (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {media.map((item) => item.kind === "photo" ? (
                          <img key={item.mediaId} src={item.thumbnailUrl || item.url} alt="" loading="lazy" onClick={() => setOpenedMedia(item)} style={{ width: 84, height: 84, objectFit: "cover", borderRadius: 10, cursor: "pointer", border: "1px solid #E8EDF5" }} />
                        ) : (
                          <video key={item.mediaId} src={item.url} preload="metadata" controls={false} onClick={() => setOpenedMedia(item)} style={{ width: 132, height: 84, objectFit: "cover", borderRadius: 10, cursor: "pointer", border: "1px solid #E8EDF5", backgroundColor: "#0F172A" }} />
                        ))}
                        {media.length === 0 ? <Typography style={{ fontSize: 12.5, color: "#64748B" }}>Medya bulunamadı.</Typography> : null}
                      </div>
                    )}
                  </div>
                ) : null}
              </SectionCard>

              {/* The customer's identity moved up beside the request number,
                  so this slot now carries the offer summary: the accepted one
                  when there is a selection, otherwise the cheapest so far. */}
              {(() => {
                const accepted = data.quotes.find((quote) => quote.quoteId === data.request.selectedQuoteId);
                const leading = accepted || [...data.quotes].sort((a, b) => (a.price || 0) - (b.price || 0))[0] || null;
                if (!leading) return null;
                return (
                  <SectionCard
                    icon={<LocalOfferOutlinedIcon sx={{ fontSize: 19 }} />}
                    title={accepted ? "Kabul edilen teklif" : "Öne çıkan teklif"}
                    iconBackground="#ECFDF5"
                    iconColor="#059669"
                  >
                    <div style={{ padding: "10px 0", borderBottom: "1px solid #F1F5F9" }}>
                      <Typography style={{ fontSize: 13, color: "#64748B", marginBottom: 6 }}>Nakliyeci</Typography>
                      <DriverLink driverId={leading.driverId} name={leading.driverName} history={history} />
                    </div>
                    {offerRows(leading).slice(0, 5).map(([rowLabel, value], index, rows) => (
                      <DetailRow key={rowLabel} label={rowLabel} value={value} last={index === rows.length - 1} />
                    ))}
                    <button type="button" onClick={() => setOpenedQuote(leading)} style={{ marginTop: 10, padding: "8px 14px", borderRadius: 10, border: "1px solid #E2E8F0", backgroundColor: "#FFFFFF", color: "#4F46E5", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                      Teklifi görüntüle
                    </button>
                  </SectionCard>
                );
              })()}

              <JourneySteps data={data} isIntercity={isIntercity} />
            </div>

            <div style={twoCol}>
              {data.selection ? (
                <SectionCard
                  icon={<LocalShippingIcon sx={{ fontSize: 19 }} />}
                  title="Seçim"
                  iconBackground="#ECFDF5"
                  iconColor="#059669"
                >
                  <div style={{ padding: "10px 0", borderBottom: "1px solid #F1F5F9" }}>
                    <Typography style={{ fontSize: 13, color: "#64748B", marginBottom: 6 }}>Nakliyeci</Typography>
                    <DriverLink driverId={data.selection.driverId} name={data.selection.moverName} history={history} />
                  </div>
                  <DetailRow label="Anlaşılan fiyat" value={money(data.selection.agreedPrice)} />
                  <DetailRow label="Taşınma tarihi" value={formatDate(data.selection.agreedMoveStartsAt)} last />
                  <div style={{ marginTop: 10, backgroundColor: "#ECFDF5", color: "#047857", borderRadius: 10, padding: "8px 12px", fontSize: 12.5, fontWeight: 700 }}>
                    Seçim sonrası müşteri ve nakliyecinin doğrudan iletişim bilgileri karşılıklı olarak açıldı.
                  </div>
                </SectionCard>
              ) : null}

              {data.contract ? (
                <SectionCard icon={<DescriptionOutlinedIcon sx={{ fontSize: 19 }} />} title="Sözleşme">
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", padding: "10px 0", borderBottom: "1px solid #F1F5F9" }}>
                    {data.contract.narrativeHtml ? (
                      <button
                        type="button"
                        onClick={() => {
                          const win = window.open("", "_blank");
                          if (win) { win.document.write(data.contract.narrativeHtml); win.document.close(); }
                        }}
                        style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 10, border: "1px solid #E2E8F0", backgroundColor: "#FFFFFF", color: "#0F172A", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
                      >
                        Sözleşmeyi Görüntüle
                      </button>
                    ) : null}
                    {data.contract.pdfUrl ? (
                      <button
                        type="button"
                        onClick={() => window.open(data.contract.pdfUrl, "_blank")}
                        style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 10, border: "none", backgroundColor: "#4F46E5", color: "#FFFFFF", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
                      >
                        PDF İndir
                      </button>
                    ) : null}
                  </div>
                  <DetailRow label="Sürüm" value={String(data.contract.version)} />
                  <DetailRow label="Durum" value={label(CONTRACT_STATUS_LABELS, data.contract.status)} />
                  <DetailRow label="Müşteri imzası" value={data.contract.customerSignedAt ? `SMS · ${formatDate(data.contract.customerSignedAt)}` : "Bekliyor"} />
                  <DetailRow label="Sürücü imzası" value={data.contract.driverSignedAt ? `SMS · ${formatDate(data.contract.driverSignedAt)}` : "Bekliyor"} />
                  <DetailRow label="PDF" value={data.contract.pdfAvailable ? `Hazır${data.contract.pdfGeneratedAt ? ` · ${formatDate(data.contract.pdfGeneratedAt)}` : ""}` : (data.contract.status === "fully_signed" ? "Onaylandı · PDF hazırlanıyor" : "İki onay sonrası oluşturulur")} />
                  <DetailRow label="Belge özeti (SHA-256)" value={data.contract.narrativeSha256 ? `${data.contract.narrativeSha256.slice(0, 18)}…` : "-"} last />
                </SectionCard>
              ) : null}

            </div>

            <SectionCard
              icon={<LocalOfferOutlinedIcon sx={{ fontSize: 19 }} />}
              title={`Teklifler (${data.quotes.length})`}
              style={{ marginTop: 20 }}
            >
              {data.quotes.length === 0 ? (
                <Typography style={{ fontSize: 13, color: "#64748B", padding: "8px 0" }}>Henüz teklif yok.</Typography>
              ) : (
                <Box sx={{ overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headCellSx}>Sürücü</TableCell>
                        <TableCell sx={headCellSx}>Telefon</TableCell>
                        <TableCell sx={headCellSx}>Fiyat</TableCell>
                        <TableCell sx={headCellSx}>Araç</TableCell>
                        <TableCell sx={headCellSx}>Durum</TableCell>
                        <TableCell sx={headCellSx}>Bloke / Ücret</TableCell>
                        <TableCell sx={headCellSx}>Tarih</TableCell>
                        <TableCell sx={headCellSx} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.quotes.map((quote) => (
                        <TableRow
                          key={quote.quoteId}
                          sx={data.request.selectedQuoteId === quote.quoteId ? { backgroundColor: "#F0FDF4" } : null}
                        >
                          <TableCell sx={bodyCellSx}>
                            <DriverLink driverId={quote.driverId} name={quote.driverName} history={history} />
                            {quote.driverRating != null ? (
                              <Typography style={{ fontSize: 12, color: "#B45309", fontWeight: 700, marginTop: 2 }}>★ {quote.driverRating}</Typography>
                            ) : null}
                          </TableCell>
                          <TableCell sx={bodyCellSx}>{quote.driverPhone}</TableCell>
                          <TableCell sx={{ ...bodyCellSx, fontWeight: 800 }}>{money(quote.price, quote.currency)}</TableCell>
                          <TableCell sx={bodyCellSx}>{label(VEHICLE_LABELS, quote.vehicle)}</TableCell>
                          <TableCell sx={bodyCellSx}>
                            {data.request.selectedQuoteId === quote.quoteId ? "Seçildi" : label(QUOTE_STATUS_LABELS, quote.status)}
                          </TableCell>
                          <TableCell sx={bodyCellSx}>
                            {quote.holdAmount != null ? `${money(quote.holdAmount)} · ${HOLD_LABELS[quote.holdStatus] || quote.holdStatus || "-"}` : "-"}
                            {(() => {
                              const refund = quote.refundedAmount != null
                                ? quote
                                : (data.contacts || []).find((c) => c.driverId === quote.driverId && c.refundedAmount != null);
                              return refund ? (
                                <Typography style={{ fontSize: 11.5, color: "#B45309", fontWeight: 700, marginTop: 3 }}>
                                  İade edildi · {money(refund.refundedAmount)}{refund.refundedAt ? ` · ${formatDate(refund.refundedAt)}` : ""}
                                </Typography>
                              ) : null;
                            })()}
                          </TableCell>
                          <TableCell sx={bodyCellSx}>{formatDate(quote.createdAt)}</TableCell>
                          <TableCell sx={bodyCellSx}>
                            <button type="button" onClick={() => setOpenedQuote(quote)} style={{ padding: "6px 12px", borderRadius: 9, border: "1px solid #E2E8F0", backgroundColor: "#FFFFFF", color: "#4F46E5", fontSize: 12.5, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
                              Teklifi görüntüle
                            </button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </SectionCard>

            <div style={twoCol}>
              <SectionCard
                icon={<CallIcon sx={{ fontSize: 19 }} />}
                title={`Müşteri Aramaları (${data.contacts.length})`}
                iconBackground="#EFF6FF"
                iconColor="#2563EB"
              >
                {data.contacts.length === 0 ? (
                  <Typography style={{ fontSize: 13, color: "#64748B", padding: "8px 0" }}>Müşteri henüz hiçbir sürücüyü aramadı.</Typography>
                ) : (
                  <Box sx={{ overflowX: "auto" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={headCellSx}>Sürücü</TableCell>
                          <TableCell sx={headCellSx}>Ücret</TableCell>
                          <TableCell sx={headCellSx}>Konuşma</TableCell>
                          <TableCell sx={headCellSx}>İade</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.contacts.map((contact) => (
                          <TableRow key={contact.contactId}>
                            <TableCell sx={bodyCellSx}>
                              <DriverLink driverId={contact.driverId} name={contact.driverName} history={history} />
                            </TableCell>
                            <TableCell sx={bodyCellSx}>
                              {contact.feeCapturedAt ? (
                                <span style={{ backgroundColor: "#DCFCE7", color: "#15803D", padding: "3px 10px", borderRadius: 8, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>
                                  Tahsil · {formatDate(contact.feeCapturedAt)}
                                </span>
                              ) : (
                                <span style={{ backgroundColor: "#F1F5F9", color: "#64748B", padding: "3px 10px", borderRadius: 8, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>
                                  Tahsil edilmedi
                                </span>
                              )}
                            </TableCell>
                            <TableCell sx={bodyCellSx}>{contact.totalCallSeconds || 0} sn</TableCell>
                            <TableCell sx={bodyCellSx}>{contact.refundRequestedAt ? (contact.refundDecisionAt ? "Karar verildi" : "İncelemede") : "-"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </SectionCard>

              <SectionCard
                icon={<AssignmentOutlinedIcon sx={{ fontSize: 19 }} />}
                title="Zaman Çizelgesi"
                iconBackground="#FEF6E7"
                iconColor="#D97706"
              >
                <DetailRow label="Oluşturulma" value={formatDate(data.request.createdAt)} />
                <DetailRow label="Teklif penceresi" value={`${formatDate(data.request.quoteWindowStartsAt)} → ${formatDate(data.request.quoteWindowEndsAt)}`} />
                {data.request.closedAt ? <DetailRow label="Kapanış" value={formatDate(data.request.closedAt)} /> : null}
                {data.request.cancelledBy || data.request.cancelReason ? (
                  <>
                    <DetailRow label="İptal eden" value={CANCELLED_BY_LABELS[data.request.cancelledBy] || data.request.cancelledBy || "-"} />
                    <DetailRow label="İptal tarihi" value={data.request.cancelledAt ? formatDate(data.request.cancelledAt) : "-"} />
                    <DetailRow label="İptal nedeni" value={data.request.cancelReason || "-"} />
                  </>
                ) : null}
                <DetailRow label="Sonuç" value={data.request.outcome ? (OUTCOME_LABELS[data.request.outcome] || data.request.outcome) : "-"} last />
                {(data.request.adminNotes || []).length ? (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed #E8EDF5" }}>
                    <Typography style={{ fontSize: 12, fontWeight: 800, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 6 }}>Operatör Notları</Typography>
                    {data.request.adminNotes.map((note, index) => (
                      <Typography key={index} style={{ fontSize: 12.5, color: "#334155", marginBottom: 4 }}>
                        <b>{note.by || "panel"}</b> · {note.at ? formatDate(note.at) : ""} — {note.note}
                      </Typography>
                    ))}
                  </div>
                ) : null}
              </SectionCard>

              {data.review ? (
                <SectionCard
                  icon={<StarIcon sx={{ fontSize: 19 }} />}
                  title="Müşteri Değerlendirmesi"
                  iconBackground="#FEF6E7"
                  iconColor="#D97706"
                >
                  <DetailRow label="Puan" value={`★ ${data.review.rating} / 5`} />
                  {data.review.comment ? <DetailRow label="Yorum" value={data.review.comment} /> : null}
                  <DetailRow label="Tarih" value={formatDate(data.review.createdAt)} last />
                </SectionCard>
              ) : null}
            </div>
          </>
        )}
      </div>

      {/* İptal — neden seçimi zorunlu */}
      {/* Lightweight offer detail panel opened from the Offers table. */}
      <Dialog open={Boolean(openedQuote)} onClose={() => setOpenedQuote(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 800, fontSize: 17 }}>
          Teklif detayları
          <Typography style={{ fontSize: 13, fontWeight: 600, color: "#64748B" }}>
            {openedQuote?.driverName || "-"}{openedQuote?.createdAt ? ` · ${formatDate(openedQuote.createdAt)}` : ""}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {openedQuote ? offerRows(openedQuote).map(([rowLabel, value], index, rows) => (
            <DetailRow key={rowLabel} label={rowLabel} value={value} last={index === rows.length - 1} />
          )) : null}
          {openedQuote?.note ? (
            <div style={{ marginTop: 10, backgroundColor: "#F8FAFC", borderRadius: 10, padding: "10px 12px" }}>
              <Typography style={{ fontSize: 12, color: "#64748B", fontWeight: 700, marginBottom: 4 }}>Nakliyeci notu</Typography>
              <Typography style={{ fontSize: 13, color: "#0F172A" }}>{openedQuote.note}</Typography>
            </div>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ padding: "0 24px 18px" }}>
          <Button onClick={() => setOpenedQuote(null)} sx={{ textTransform: "none", fontWeight: 700 }}>Detayı kapat</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={actionDialog === "cancel"} onClose={() => !actionBusy && setActionDialog(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 800, fontSize: 17 }}>Talebi İptal Et</DialogTitle>
        <DialogContent>
          <Typography style={{ fontSize: 13, color: "#64748B", marginBottom: 14 }}>
            Talep kapatılır ve müşteri bilgilendirilir. İptal nedeni seçmek zorunludur.
          </Typography>
          <TextField
            select fullWidth size="small" label="İptal nedeni" value={cancelReason}
            onChange={(event) => setCancelReason(event.target.value)}
          >
            {CANCEL_REASONS.map((reason) => (
              <MenuItem key={reason} value={reason}>{reason}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ padding: "0 24px 18px" }}>
          <Button onClick={() => setActionDialog(null)} disabled={actionBusy} sx={{ textTransform: "none", fontWeight: 700, color: "#64748B" }}>Vazgeç</Button>
          <Button
            variant="contained" disableElevation disabled={actionBusy || !cancelReason}
            onClick={() => runAction("cancel", { cancelReason })}
            sx={{ textTransform: "none", fontWeight: 800, backgroundColor: "#B91C1C", "&:hover": { backgroundColor: "#991B1B" }, borderRadius: "10px" }}
          >
            {actionBusy ? "İptal ediliyor…" : "Talebi İptal Et"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Düzenle — tarih / ödeme tercihi / not */}
      <Dialog open={actionDialog === "edit"} onClose={() => !actionBusy && setActionDialog(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 800, fontSize: 17 }}>Talebi Düzenle</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth size="small" type="date" label="Taşınma tarihi" value={editDate}
            onChange={(event) => setEditDate(event.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ marginTop: 1 }}
          />
          <TextField
            select fullWidth size="small" label="Ödeme tercihi" value={editPayment}
            onChange={(event) => setEditPayment(event.target.value)} sx={{ marginTop: 2 }}
          >
            <MenuItem value="">Değiştirme</MenuItem>
            <MenuItem value="cash">Nakit</MenuItem>
            <MenuItem value="card">Kredi kartı</MenuItem>
            <MenuItem value="either">Fark etmez</MenuItem>
          </TextField>
          <TextField
            fullWidth size="small" multiline minRows={2} label="Düzenleme notu (opsiyonel)" value={noteText}
            onChange={(event) => setNoteText(event.target.value)} sx={{ marginTop: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ padding: "0 24px 18px" }}>
          <Button onClick={() => setActionDialog(null)} disabled={actionBusy} sx={{ textTransform: "none", fontWeight: 700, color: "#64748B" }}>Vazgeç</Button>
          <Button
            variant="contained" disableElevation disabled={actionBusy || (!editDate && !editPayment && !noteText.trim())}
            onClick={() => runAction("edit", { movingDate: editDate || undefined, paymentPreference: editPayment || undefined, scheduleNote: noteText.trim() || undefined })}
            sx={{ textTransform: "none", fontWeight: 800, backgroundColor: "#4F46E5", "&:hover": { backgroundColor: "#4338CA" }, borderRadius: "10px" }}
          >
            {actionBusy ? "Kaydediliyor…" : "Kaydet"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Not ekle */}
      <Dialog open={actionDialog === "note"} onClose={() => !actionBusy && setActionDialog(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 800, fontSize: 17 }}>Operatör Notu Ekle</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth size="small" multiline minRows={3} autoFocus label="Not" value={noteText}
            onChange={(event) => setNoteText(event.target.value)} sx={{ marginTop: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ padding: "0 24px 18px" }}>
          <Button onClick={() => setActionDialog(null)} disabled={actionBusy} sx={{ textTransform: "none", fontWeight: 700, color: "#64748B" }}>Vazgeç</Button>
          <Button
            variant="contained" disableElevation disabled={actionBusy || !noteText.trim()}
            onClick={() => runAction("note", { note: noteText.trim() })}
            sx={{ textTransform: "none", fontWeight: 800, backgroundColor: "#4F46E5", "&:hover": { backgroundColor: "#4338CA" }, borderRadius: "10px" }}
          >
            {actionBusy ? "Ekleniyor…" : "Notu Ekle"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Medya tam ekran görüntüleyici */}
      <Dialog open={!!openedMedia} onClose={() => setOpenedMedia(null)} maxWidth="md">
        {openedMedia ? (
          <DialogContent sx={{ padding: 1.5, backgroundColor: "#0F172A", display: "flex", justifyContent: "center" }}>
            {openedMedia.type === "video" ? (
              <video src={openedMedia.url} controls autoPlay style={{ maxWidth: "100%", maxHeight: "78vh", borderRadius: 8 }} />
            ) : (
              <img src={openedMedia.url} alt="Müşteri medyası" style={{ maxWidth: "100%", maxHeight: "78vh", borderRadius: 8, objectFit: "contain" }} />
            )}
          </DialogContent>
        ) : null}
      </Dialog>
    </div>
  );
}
